/**
 * PublicSeriesCard Component
 *
 * Displays a public series card for the Explore page and Search results.
 * Includes author info, book count, and engagement metrics.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Users, Heart, Eye, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const PublicSeriesCard = ({ series, onClick, onFavoriteChange }) => {
  const navigate = useNavigate();
  const [isFavorited, setIsFavorited] = useState(series.is_favorited || false);
  const [favoritesCount, setFavoritesCount] = useState(series.favorites_count || 0);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/series/public/${series.id}`);
    }
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (isTogglingFavorite) return;

    setIsTogglingFavorite(true);
    try {
      const response = await axios.post(
        `${API}/v1/series/${series.id}/favorite`,
        {},
        { withCredentials: true }
      );

      setIsFavorited(response.data.favorited);
      setFavoritesCount(response.data.favorites_count);

      if (onFavoriteChange) {
        onFavoriteChange(series.id, response.data.favorited, response.data.favorites_count);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please log in to favorite series');
      } else {
        toast.error('Failed to update favorite');
      }
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  return (
    <div
      onClick={handleClick}
      className="rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] group"
      style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
    >
      {/* Cover Image */}
      <div
        className="relative aspect-[4/3] overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #E0E7FF 0%, #C7D2FE 100%)' }}
      >
        {series.cover_image_url ? (
          <img
            src={getImageUrl(series.cover_image_url)}
            alt={series.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-10 h-10" style={{ color: '#818CF8' }} />
          </div>
        )}

        {/* Book Count Badge */}
        <div
          className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 bg-white/95 dark:bg-gray-800/95 text-indigo-600 dark:text-indigo-400"
        >
          <BookOpen className="w-3 h-3" />
          {series.book_count || 0} {series.book_count === 1 ? 'book' : 'books'}
        </div>

        {/* Story Format Badge */}
        <div
          className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/95 dark:bg-gray-800/95"
          style={{ color: 'var(--st-text-heading)' }}
        >
          {{ '3-5': 'Picture Book', '5-8': 'Easy Reader', '8-12': 'Chapter Book' }[series.age_range] || series.age_range}
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Title */}
        <h3
          className="font-bold text-sm mb-1.5 line-clamp-1"
          style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
        >
          {series.title}
        </h3>

        {/* Description */}
        {series.description && (
          <p
            className="text-[11px] line-clamp-2 mb-2"
            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
          >
            {series.description}
          </p>
        )}

        {/* Author Row */}
        <div className="flex items-center gap-2 mb-2">
          {series.author_picture ? (
            <img
              src={getImageUrl(series.author_picture)}
              alt={series.author_name}
              className="w-5 h-5 rounded-full object-cover"
            />
          ) : (
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center"
              style={{ background: 'var(--st-border-light)' }}
            >
              <User className="w-3 h-3" style={{ color: 'var(--st-primary)' }} />
            </div>
          )}
          <span
            className="text-xs font-semibold truncate"
            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
          >
            {series.author_name || 'Anonymous'}
          </span>
        </div>

        {/* Stats Row */}
        <div className="flex items-center gap-3 text-[11px]" style={{ color: 'var(--st-text-muted)' }}>
          {/* Character Count */}
          {series.characters?.length > 0 && (
            <div className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              <span className="font-medium">{series.characters.length}</span>
            </div>
          )}

          {/* Favorites - Clickable */}
          <button
            onClick={handleFavoriteClick}
            disabled={isTogglingFavorite}
            className="flex items-center gap-1 transition-all hover:scale-110 disabled:opacity-50"
            title={isFavorited ? 'Unfavorite' : 'Favorite'}
          >
            <Heart
              className="w-3.5 h-3.5 transition-colors"
              style={{
                color: isFavorited ? '#EF4444' : 'var(--st-text-muted)',
                fill: isFavorited ? '#EF4444' : 'none'
              }}
            />
            <span className="font-medium">{favoritesCount}</span>
          </button>

          {/* Views */}
          <div className="flex items-center gap-1">
            <Eye className="w-3.5 h-3.5" />
            <span className="font-medium">{series.views_count || 0}</span>
          </div>

          {/* Art Style */}
          <span
            className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize bg-gray-200 dark:bg-gray-700"
            style={{ color: 'var(--st-text-heading)' }}
          >
            {series.art_style?.replace('_', ' ')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PublicSeriesCard;
